import type { Signal } from '@preact/signals-react'
import type { EventProperties } from '@segment/analytics-next'

import { analytics } from './segment'

let avoLoadedSignal: Signal<boolean | undefined> | undefined

/**
 * Sets the Avo loaded signal for tracking Avo Inspector initialization state.
 * @param signal - The signal to track Avo loaded state
 * @returns void
 */
export const setAvoLoadedSignal = (
  signal: Signal<boolean | undefined>,
): void => {
  avoLoadedSignal = signal
}

/**
 * Segment destination interface for Avo Inspector integration.
 * Provides methods for tracking events, identifying users, and logging pages.
 */
export interface SegmentDestination {
  /** Initializes the destination and marks Avo as loaded */
  make: () => void
  /** Logs a custom event to Segment */
  logEvent: (eventName: string, eventProperties: EventProperties) => void
  /** Sets user properties for a specific user */
  setUserProperties: (userId: string, userProperties: object) => void
  /** Identifies a user by their ID */
  identify: (userId: string) => void
  /** Unidentifies the current user */
  unidentify: () => void
  /** Logs a page view with optional properties */
  logPage: (pageName: string, eventProperties: EventProperties) => void
  /** Tracks a revenue event */
  revenue: (amount: number, eventProperties: EventProperties) => void
}

/**
 * Creates a Segment destination object for Avo Inspector integration.
 * This function is safe to call in SSR environments and will only execute browser-specific code when appropriate.
 * @returns SegmentDestination object with methods for tracking and user identification
 */
export const createSegmentDestination = (): SegmentDestination => {
  return {
    make: (): void => {
      if (typeof document === 'undefined') {
        return
      }

      // Set the avoLoaded signal if provided
      if (avoLoadedSignal) {
        avoLoadedSignal.value = true
      }
      document.dispatchEvent(new Event('avo_inspector_initialized'))
    },
    logEvent: (eventName: string, eventProperties: EventProperties): void => {
      analytics.track(eventName, eventProperties)
    },
    setUserProperties: (userId: string, userProperties: object): void => {
      analytics.identify(userId, userProperties)
    },
    identify: (userId: string): void => {
      analytics.identify(userId)
    },
    unidentify: (): void => {
      analytics.identify(null)
    },
    logPage: (pageName: string, eventProperties: EventProperties): void => {
      const properties = { ...eventProperties, pageName }

      analytics.page(properties)
    },
    revenue: (amount: number, eventProperties: EventProperties): void => {
      const name = 'Purchase Complete'
      const properties = { ...eventProperties, revenue: amount }

      analytics.track(name, properties)
    },
  }
}

/**
 * Default Segment destination instance for backward compatibility.
 * Creates a new instance each time it's accessed. For new code, prefer using createSegmentDestination().
 * @deprecated Use createSegmentDestination() instead for better control over instance creation
 */
export const segmentDestination: SegmentDestination = createSegmentDestination()
